import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircle } from "lucide-react"
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { authSchemas, useSignUpEmail } from "@/api/client/auth"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { cn } from "@/lib/utils"

const signupSchema = authSchemas.signUpEmailRequest
  .omit({ name: true, callbackURL: true })
  .extend({
    emailConfirmation: z
      .string()
      .min(1, "メールアドレス（確認用）を入力してください")
      .email("有効なメールアドレスを入力してください"),
  })
  .refine((data) => data.email === data.emailConfirmation, {
    message: "メールアドレスが一致しません",
    path: ["emailConfirmation"],
  })

type SignupFormData = z.infer<typeof signupSchema>

interface Step {
  number: number
  label: string
}

const STEPS: Step[] = [
  { number: 1, label: "新規情報\n登録" },
  { number: 2, label: "利用契約書\n確認" },
  { number: 3, label: "利用規約\n確認" },
  { number: 4, label: "完了" },
]

const SERVICE_AGREEMENT_CONTENT = `オーナー向け利用契約書（ダミー）

第1条（目的）
本契約書は、エージェントコールのオーナー向けサービスをご利用いただくにあたり、サービスの内容、利用条件、料金、注意事項その他必要な事項を定めるものです。

第2条（サービス内容）
本サービスでは、物件・部屋・入居者情報の管理、問い合わせ対応、各種通知、その他当社が提供する関連機能をご利用いただけます。実際の契約書本文は後日正式な内容に差し替えます。

第3条（利用開始）
利用者は、本契約書の内容を確認し、同意したうえでアカウント登録を完了するものとします。登録情報に変更が生じた場合は、速やかに最新の情報へ更新してください。

第4条（利用料金）
本サービスの利用料金、支払方法、請求時期等は、別途当社が定める内容に従うものとします。料金に関する正式な条項は後日入稿予定です。

第5条（禁止事項）
利用者は、虚偽情報の登録、第三者への不正利用許可、システムへの不正アクセス、法令または公序良俗に反する行為を行ってはなりません。

第6条（免責）
当社は、天災、通信障害、システム保守、その他やむを得ない事由によりサービス提供が一時的に停止した場合の損害について、当社の責に帰すべき事由がある場合を除き責任を負いません。

第7条（契約内容の変更）
本契約書の内容は、サービス改善や法令対応等のため変更される場合があります。変更後の内容は、当社所定の方法で通知または掲載します。

以上`

const TERMS_OF_USE_CONTENT = `オーナー向け利用規約（ダミー）

第1条（適用）
本利用規約は、エージェントコールのオーナー向けサービスの利用に関する条件を定めるものです。利用者は、本規約に同意したうえでサービスを利用します。

第2条（アカウント管理）
利用者は、登録メールアドレスおよびパスワードを自己の責任で適切に管理するものとします。第三者による不正利用を防止するため、認証情報を厳重に取り扱ってください。

第3条（登録情報）
利用者は、登録時および利用中に提供する情報について、正確かつ最新の内容を維持するものとします。登録情報に誤りがある場合、サービスの一部を利用できないことがあります。

第4条（禁止行為）
利用者は、法令違反、不正アクセス、他者への迷惑行為、虚偽情報の登録、サービス運営を妨げる行為、その他当社が不適切と判断する行為を行ってはなりません。

第5条（サービスの変更・停止）
当社は、必要に応じてサービス内容の変更、追加、停止を行うことがあります。重要な変更がある場合は、当社所定の方法により通知します。

第6条（個人情報の取扱い）
当社は、利用者の個人情報をプライバシーポリシーおよび関連法令に従って適切に取り扱います。正式な規約本文は後日入稿予定です。

第7条（準拠法）
本規約は日本法に準拠します。本サービスに関して紛争が生じた場合は、当社所在地を管轄する裁判所を第一審の専属的合意管轄裁判所とします。

以上`

function StepProgress({ currentStep }: { currentStep: number }) {
  return (
    <div className="relative w-full px-8">
      <div className="absolute top-[17px] left-[58px] right-[58px] h-[2px] bg-slate-200" />
      <div className="relative flex items-start justify-between">
        {STEPS.map((step) => {
          const isCurrent = step.number === currentStep
          const isComplete = step.number < currentStep

          return (
            <div key={step.number} className="flex w-14 flex-col items-center gap-2">
              <div
                className={cn(
                  "relative z-10 flex size-[34px] items-center justify-center rounded-full text-sm font-bold",
                  isCurrent || isComplete
                    ? "bg-[#354267] text-white"
                    : "border-2 border-slate-200 bg-slate-300 text-slate-500"
                )}
              >
                {step.number}
              </div>
              <span className="whitespace-pre-line text-center text-xs font-bold leading-[14px] text-slate-600">
                {step.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ScrollableAgreementSection({
  title,
  content,
  checkboxLabel,
  isAgreed,
  onAgreedChange,
  onNext,
  onBack,
  isNextPending = false,
  nextButtonLabel = "次へ",
}: {
  title: string
  content: string
  checkboxLabel: ReactNode
  isAgreed: boolean
  onAgreedChange: (agreed: boolean) => void
  onNext: () => void
  onBack: () => void
  isNextPending?: boolean
  nextButtonLabel?: string
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10

    if (isAtBottom) {
      setHasScrolledToBottom(true)
    }
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    handleScroll()
    container.addEventListener("scroll", handleScroll)
    return () => container.removeEventListener("scroll", handleScroll)
  }, [handleScroll])

  return (
    <div className="w-full max-w-sm space-y-5">
      <h2 className="text-[15px] font-semibold tracking-[-0.3px] text-slate-600">{title}</h2>
      <Card className="h-[365px] border-none shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
        <div ref={scrollContainerRef} className="h-full overflow-y-auto">
          <div className="p-[15px]">
            <p className="whitespace-pre-line text-xs font-normal leading-5 text-slate-600">
              {content}
            </p>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-center bg-slate-100 py-[17px]">
        <label
          className={cn(
            "flex items-start gap-[6px] px-4",
            hasScrolledToBottom ? "cursor-pointer" : "cursor-not-allowed opacity-50"
          )}
        >
          <Checkbox
            checked={isAgreed}
            onCheckedChange={(checked) => onAgreedChange(checked === true)}
            disabled={!hasScrolledToBottom}
            className="mt-[2px] size-[18px] rounded border-slate-300 data-[state=checked]:border-[#354267] data-[state=checked]:bg-[#354267]"
          />
          <span className="text-sm font-medium leading-5 tracking-[-0.168px] text-slate-600">
            {checkboxLabel}
          </span>
        </label>
      </div>

      <div className="flex flex-col gap-[15px] pt-5">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isNextPending}
          className="h-[50px] w-full rounded-[6px] border-[#9eafc7] text-sm font-bold text-[#354267]"
        >
          戻る
        </Button>
        <Button
          type="button"
          disabled={!isAgreed || isNextPending}
          onClick={onNext}
          className={cn(
            "h-[50px] w-full rounded-[6px] text-sm font-bold",
            isAgreed && !isNextPending
              ? "bg-[#354267] text-white hover:bg-[#354267]/90"
              : "cursor-not-allowed bg-slate-200 text-slate-400"
          )}
        >
          {isNextPending ? "登録中..." : nextButtonLabel}
        </Button>
      </div>
    </div>
  )
}

function CompletionStep() {
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-8 px-4 pt-8 text-center">
      <CheckCircle className="size-16 text-[#354267]" />
      <div className="space-y-3">
        <h2 className="text-xl font-bold text-slate-700">メールアドレス認証を行ってください</h2>
        <p className="text-sm leading-6 text-slate-500">
          ご登録のメールアドレス宛に認証メールを送信しました。メール内のリンクを開いて認証を完了してください。
        </p>
      </div>
    </div>
  )
}

export function SignupPage() {
  const [currentStep, setCurrentStep] = useState(1)
  const [agreementAccepted, setAgreementAccepted] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      lastName: "",
      firstName: "",
      email: "",
      emailConfirmation: "",
      phoneNumber: "",
      emergencyPhoneNumber: "",
      address: "",
      password: "",
    },
  })

  const { mutateAsync: signUpEmail, isPending } = useSignUpEmail({
    onSuccess: () => {
      toast.success("アカウントの作成に成功しました")
      setCurrentStep(4)
    },
    onError: () => {
      toast.error("アカウントの作成に失敗しました。もう一度お試しください。")
    },
  })

  const handleInformationComplete = async () => {
    const isValid = await form.trigger()
    if (isValid) {
      setCurrentStep(2)
    }
  }

  const handleCreateAccount = async () => {
    const isValid = await form.trigger()
    if (!isValid || !agreementAccepted || !termsAccepted) return

    const data = form.getValues()
    try {
      await signUpEmail({
        ...data,
        name: `${data.lastName} ${data.firstName}`,
        callbackURL: `${window.location.origin}/signin`,
      })
    } catch (_error) {
      // 表示は onError が担当する
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="flex flex-1 flex-col items-center px-4 pb-8">
        <div className="mb-8 w-full max-w-sm text-center">
          <img src={logo} alt="エージェントコール" className="mb-4 w-full" />
          <p className="text-sm leading-relaxed text-slate-500">
            複数の物件や部屋を簡単に管理し
            <br />
            入居者とのコミュニケーションを効率化します。
          </p>
        </div>

        <div className="mb-8 w-full max-w-sm">
          <StepProgress currentStep={currentStep} />
        </div>

        {currentStep === 1 && (
          <div className="w-full max-w-sm">
            <h2 className="mb-8 text-center text-lg font-bold text-slate-700">ユーザー新規登録</h2>

            <Form {...form}>
              <form className="space-y-5">
                <div className="flex gap-2.5">
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="text-sm font-semibold text-slate-600">
                          氏名（姓）
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="山田" className="h-11" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="text-sm font-semibold text-slate-600">
                          氏名（名）
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="太郎" className="h-11" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        メールアドレス
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="name@company.jp"
                          type="email"
                          autoComplete="email"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="emailConfirmation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        メールアドレス（確認用）
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="name@company.jp"
                          type="email"
                          autoComplete="email"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        電話番号
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="01234567890"
                          type="tel"
                          autoComplete="tel"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="emergencyPhoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        緊急用電話番号（任意）
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="09012345678"
                          type="tel"
                          autoComplete="tel"
                          maxLength={11}
                          className="h-11"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "")
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">住所</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="東京都渋谷区..."
                          autoComplete="street-address"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        パスワード
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="****" type="password" className="h-11" {...field} />
                      </FormControl>
                      <p className="text-sm text-slate-400">
                        半角英数字・記号を含めて入力してください
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4 pt-3">
                  <Button
                    type="button"
                    className="h-11 w-full font-bold"
                    onClick={handleInformationComplete}
                  >
                    次へ
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 w-full font-bold text-primary"
                    asChild
                  >
                    <Link to="/signin">ログイン画面に戻る</Link>
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}

        {currentStep === 2 && (
          <ScrollableAgreementSection
            title="オーナー向け利用契約書"
            content={SERVICE_AGREEMENT_CONTENT}
            checkboxLabel="利用契約書に同意する"
            isAgreed={agreementAccepted}
            onAgreedChange={setAgreementAccepted}
            onBack={() => setCurrentStep(1)}
            onNext={() => setCurrentStep(3)}
          />
        )}

        {currentStep === 3 && (
          <ScrollableAgreementSection
            title="オーナー向け利用規約"
            content={TERMS_OF_USE_CONTENT}
            checkboxLabel="利用規約に同意する"
            isAgreed={termsAccepted}
            onAgreedChange={setTermsAccepted}
            onBack={() => setCurrentStep(2)}
            onNext={handleCreateAccount}
            isNextPending={isPending}
            nextButtonLabel="登録する"
          />
        )}

        {currentStep === 4 && <CompletionStep />}
      </div>
    </div>
  )
}
